import { ICdsOptions, IDatasetControlProps } from "./interfaces";
import { ILayout } from "@talxis/client-libraries";
import { Form } from "../native/Form/interfaces/form";
import { ControlLoader } from "@src/app/classes/loaders/ControlLoader";
import { merge } from "@fluentui/utilities";
import { IFormContext } from "../native/Form/interfaces/IFormContext";
import { ICustomControl as ICustomControlDefinition } from "../interfaces/customcontrol";
import { ViewDefinition } from "@src/app/classes/definitions/ViewDefinition";
import { IViewDefinition } from "../native/View/interfaces/viewdefinition";

interface IEvents {
    onDependenciesLoaded?: (dependencies: DatasetDependencies) => void;
}

interface ICustomControl {
    name: string;
    displayName: string;
    default: boolean;
    bindings: {
        [name: string]: Form.ParameterBinding;
    }
}

export class DatasetDependencies {
    private _entityName: string;
    private _controlProps: IDatasetControlProps;
    private _customControls: ICustomControl[] = [];
    private _controlDefinition: ICustomControlDefinition;
    private _viewDefinition: IViewDefinition;
    private _controlInstance: ComponentFramework.StandardControl<any, any>;
    private _cdsOptions: ICdsOptions;
    private _formContext?: IFormContext;
    private _events?: IEvents;
    private _customControlDescriptions: any[];

    constructor(entityName: string, controlProps: IDatasetControlProps, events: IEvents, formContext?: IFormContext) {
        this._entityName = entityName;
        this._controlProps = controlProps;
        this._events = events;
        this._formContext = formContext;
        this._init();
    }

    public getControlInstance() {
        return this._controlInstance;
    }

    public getControlBindings() {
        return this.getControlProps().bindings;
    }

    public getCustomControls(): ICustomControl[] {
        return this._customControls;
    }

    public getCustomControlDescriptions(): any[] {
        return this._customControlDescriptions;
    }

    public getCurrentCustomControl(): ICustomControl {
        return this.getCustomControls().find(x => x.default) ?? this.getCustomControls()[0];
    }

    public getControlDefinition(): ICustomControlDefinition {
        return this._controlDefinition;
    }

    public getEntityName(): string {
        return this._entityName;
    }

    public getDatasetBindingName(): string {
        return this._controlDefinition.manifest.datasets[0].name;
    }

    public getFormContext(): IFormContext | undefined {
        return this._formContext;
    }

    public getCdsOptions() {
        return this._cdsOptions;
    }

    public areUserQueriesEnabled() {
        return this.getControlBindings().EnableUserQueries?.value === 'true';
    }

    public getViewDefinition() {
        return this._viewDefinition;
    }

    private async _init() {
        await this._loadControl();
        this._cdsOptions = this._getCdsOptions();
        this._events.onDependenciesLoaded(this);
    }

    private async _loadControl() {
        const viewId = await this._getInitialViewId(this._entityName);
        this._viewDefinition = await ViewDefinition.getAsync(viewId);
        this._customControls = this._getCustomControls(this._viewDefinition.layoutjson);
        const controlName = this.getCurrentCustomControl().name;
        this._controlDefinition = await (await ControlLoader.getAsync(controlName)).load();
        //@ts-ignore - constructor not part of typings
        this._controlInstance = new this._controlDefinition.code(this._isLegacyView());
    }

    private _isLegacyView() {
        const bindings = this.getControlBindings();
        if (bindings.DisableNewGrid?.value === 'true' || bindings.EnableGroupBy?.value === 'true') {
            return true;
        }
        return false;
    }

    private _getCustomControls(layoutJSON: string): ICustomControl[] {
        const layout = JSON.parse(layoutJSON) as ILayout;
        this._customControlDescriptions = layout.CustomControlDescriptions;
        if (!this._customControlDescriptions || this._customControlDescriptions.length === 0) {
            return [{
                name: this._controlProps.name,
                displayName: this._controlProps.name,
                bindings: {},
                default: true,
            }];
        }
        const controlMap = new Map(this._customControlDescriptions[0].CustomControls.map((control: any) => [control.CustomControlId, control]));
        const customControls: ICustomControl[] = [];
        [...controlMap.values()].map((control: any) => {
            customControls.push({
                name: control.Name,
                displayName: control.DisplayNameKey,
                default: control.FormFactor != -1,
                bindings: (() => {
                    const result: { [name: string]: Form.ParameterBinding } = {};
                    control.Parameters.map((par: any) => {
                        if (par.Value.Static === undefined) {
                            return;
                        }
                        result[par.Name] = {
                            isStatic: par.Value.Static,
                            value: par.Value.Value,
                            type: par.Value.Type,
                        };
                    });
                    return result;
                })()
            });
        });
        return customControls;
    }

    private _getCdsOptions(): ICdsOptions | null {
        const controlProps = this.getControlProps();
        const inputString = this._controlDefinition.manifest.datasets[0].cdsOptions;
        let jsonObject: any = {};
        if (!inputString) {
            //the default values should correspond to the default subgrid settings
            jsonObject = {
                displayCommandBar: true,
                displayIndex: false,
                displayPaging: true,
                displayQuickFind: false,
                displayViewSelector: false,
            } as ICdsOptions;
        }
        else {
            //lowest priority => cdsOptions from manifest
            try {
                const keyValuePairs = inputString.split(';');
                for (const pair of keyValuePairs) {
                    if (!pair) continue;
                    const [key, value] = pair.split(':');
                    const formattedKey = `${key.trim()}`; // Add double quotes around the key
                    jsonObject[formattedKey] = JSON.parse(value.trim()); // Parse the value and add it to the object
                }
            }
            catch (err) {
                throw new Error(`Invalid cdsOptions parameter in control ${controlProps.name}`);
            }
        }
        //medium priority => virtual grid dataset binding
        const cdsOptions = {
            ...jsonObject,
            ...controlProps.bindings[this.getDatasetBindingName()]?.DataSetUIOptions
        } as ICdsOptions;
        //highest priority => bindings for specific attributes 
        if (controlProps.bindings.EnableQuickFind) {
            cdsOptions.displayQuickFind = controlProps.bindings.EnableQuickFind.value === 'true';
        }
        if (controlProps.bindings.EnableViewPicker) {
            cdsOptions.displayViewSelector = controlProps.bindings.EnableViewPicker.value === 'true';
        }
        if (controlProps.bindings.DisplayCommandBar) {
            cdsOptions.displayCommandBar = controlProps.bindings.DisplayCommandBar.value === 'true';
        }
        if (controlProps.bindings.DisplayPaging) {
            cdsOptions.displayPaging = controlProps.bindings.DisplayPaging.value === 'true';
        }
        return cdsOptions;
    }

    private async _getInitialViewId(entityName: string) {
        const bindings = this.getControlBindings();
        if (bindings.ViewId?.value) {
            return bindings.ViewId.value;
        }
        for (const binding of Object.values(bindings)) {
            if (binding.ViewId) {
                return binding.ViewId;
            }
        }
        const availableViews = await ViewDefinition.getViewNamesAndIds(entityName, { viewIds: bindings?.ViewIds?.value.split(','), includeUserQueries: this.areUserQueriesEnabled() });
        if (availableViews.length > 0) {
            return availableViews[0].queryid;
        }
        throw new Error(`Not view found for entity ${entityName}!`);
    }

    public getControlProps(): IDatasetControlProps {
        if (this.getCustomControls().length === 0) {
            return this._controlProps;
        }
        //priority: lowest => default props or props set on a subgrid, medium => bindings set on a view , highest => url bindings
        const mergedBindings = merge({}, this._controlProps.bindings, this.getCurrentCustomControl().bindings, this._controlProps.urlBindings ?? {});
        return {
            ...this._controlProps,
            bindings: mergedBindings
        };
    }
}